import React, { Fragment, useState, useRef, useEffect, useLayoutEffect } from 'react';
import cx from 'classnames';
import { useCurrentEditor } from '@tiptap/react';
import { ChevronDown } from 'lucide-react';
import { computePosition, autoUpdate, offset, flip, shift } from '@floating-ui/dom';
import { getToolbarItems, type MarkdownAction } from './toolbarConfig';
import { LinkToolbarPopover } from './LinkToolbarPopover';
import { MarkdownToolbarIcon } from './MarkdownToolbarIcon';
import { ToolbarTooltip, type ShortcutToken } from './ToolbarTooltip';
import { PickerButton } from '../../../views/SidebarPanel/Detailbar/CommentsAndLog/Comments/CommentStream/Comment/Reactions/PickerButton';
import type { EmojiPickerConfig } from '../types';
import styles from './Toolbar.module.css';

const MARKDOWN_TOGGLE_SHORTCUT: ShortcutToken[] = ['mod', 'alt', 'shift', 'M'];

const ToolbarButton = ({
  onClick,
  active,
  title,
  shortcut,
  children,
  preventMousedownBlur
}: {
  onClick: () => void;
  active?: boolean;
  title: string;
  shortcut?: ShortcutToken[];
  children: React.ReactNode;
  preventMousedownBlur?: boolean;
}) => (
  <ToolbarTooltip label={title} shortcut={shortcut}>
    <button
      type="button"
      className={`${styles.button} ${active ? styles.active : ''}`}
      onClick={onClick}
      onMouseDown={preventMousedownBlur ? e => { e.preventDefault(); } : undefined}
    >
      {children}
    </button>
  </ToolbarTooltip>
);

const DropdownButton = ({
  label,
  title,
  active,
  children,
  isOpen,
  onToggle,
  variant
}: {
  label: React.ReactNode;
  title: string;
  active?: boolean;
  children: React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
  variant?: 'bottom';
}) => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onToggle();
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onToggle]);

  useLayoutEffect(() => {
    if (!isOpen) return;
    const trigger = ref.current?.querySelector('button');
    const menu = ref.current?.querySelector(`.${styles.dropdownMenu}`) as HTMLElement | null;
    if (!trigger || !menu) return;

    const updatePosition = () => {
      computePosition(trigger, menu, {
        placement: variant === 'bottom' ? 'top-end' : 'bottom-start',
        strategy: 'fixed',
        middleware: [offset(4), flip({ padding: 8 }), shift({ padding: 8 })]
      }).then(({ x, y, strategy }) => {
        Object.assign(menu.style, {
          position: strategy,
          left: `${x}px`,
          top: `${y}px`,
          right: 'auto',
          bottom: 'auto',
          marginTop: '0'
        });
      });
    };

    updatePosition();
    return autoUpdate(trigger, menu, updatePosition, {
      ancestorScroll: true,
      ancestorResize: true,
      elementResize: true,
      layoutShift: true
    });
  }, [isOpen, variant]);

  return (
    <div ref={ref} className={styles.dropdown}>
      <ToolbarTooltip label={title}>
        <button
          type="button"
          className={`${styles.button} ${styles.dropdownTrigger} ${active ? styles.active : ''}`}
          onClick={onToggle}
        >
          {label}
          <ChevronDown size={12} className={styles.chevron} />
        </button>
      </ToolbarTooltip>
      {isOpen && (
        <div className={`${styles.popoverSurface} ${styles.popoverSurfaceRaised} ${styles.dropdownMenu}`}>
          {children}
        </div>
      )}
    </div>
  );
};

export type MarkdownSourceContext = {
  getValue: () => string;
  getSelection: () => { start: number; end: number };
  apply: (next: string, start: number, end: number) => void;
};

export const Toolbar = ({
  emojiPickerConfig = null,
  variant,
  panelCompact = false,
  sidebarTightViewport = false,
  sidebarFillToolbarWidth = false,
  narrowViewportToolbarScroll = false,
  markdownSourceModeActive = false,
  markdownEditPreferred = false,
  markdownSource = null,
  onMarkdownToggle,
  onEmojiInsertInMarkdownSource
}: {
  emojiPickerConfig?: EmojiPickerConfig | null;
  variant?: 'bottom';
  panelCompact?: boolean;
  sidebarTightViewport?: boolean;
  sidebarFillToolbarWidth?: boolean;
  narrowViewportToolbarScroll?: boolean;
  markdownSourceModeActive?: boolean;
  markdownEditPreferred?: boolean;
  markdownSource?: MarkdownSourceContext | null;
  onMarkdownToggle?: () => void;
  onEmojiInsertInMarkdownSource?: (emojiNative: string) => void;
}) => {
  const { editor } = useCurrentEditor();
  const [openId, setOpenId] = useState<string | null>(null);
  const [emojiPopoverOpen, setEmojiPopoverOpen] = useState(false);

  if (!editor) return null;

  const items = getToolbarItems(editor);

  const openOnly = (id: string) => {
    setOpenId(prev => (prev === id ? null : id));
  };

  const runMarkdownAction = (action?: MarkdownAction) => {
    if (!markdownSource || !action) return;
    const text = markdownSource.getValue();
    const { start, end } = markdownSource.getSelection();
    const result = action(text, start, end);
    markdownSource.apply(result.text, result.start, result.end);
  };

  const bottomToolbarClass =
    variant === 'bottom'
      ? cx(styles.toolbarBottom, {
          [styles.toolbarPanelCompact]: panelCompact,
          [styles.toolbarBottomExpandedLayout]:
            !panelCompact && !narrowViewportToolbarScroll,
          [styles.toolbarBottomScrollableNarrow]:
            !panelCompact && narrowViewportToolbarScroll,
          [styles.toolbarSidebarTightViewport]:
            panelCompact && sidebarTightViewport,
          [styles.toolbarSidebarFillWidth]:
            panelCompact && sidebarFillToolbarWidth && !sidebarTightViewport
        })
      : undefined;

  const markdownButton = onMarkdownToggle ? (
    <ToolbarButton
      onClick={onMarkdownToggle}
      active={markdownEditPreferred}
      title={
        markdownEditPreferred
          ? 'Rich text (Markdown source editing is on — click to use the visual editor)'
          : 'Edit as Markdown'
      }
      shortcut={MARKDOWN_TOGGLE_SHORTCUT}
      preventMousedownBlur
    >
      <MarkdownToolbarIcon size={14} />
    </ToolbarButton>
  ) : null;

  const emojiButton = emojiPickerConfig ? (
    <PickerButton
      getContainer={emojiPickerConfig.getContainer}
      onEmojiSelect={emoji => {
        if (onEmojiInsertInMarkdownSource) {
          onEmojiInsertInMarkdownSource(emoji.native);
        } else {
          editor.commands.insertContent(emoji.native);
        }
        setEmojiPopoverOpen(false);
      }}
      tooltipTitle="Add emoji"
      placement="bottomLeft"
      setPopoverOpen={setEmojiPopoverOpen}
      popoverOpen={emojiPopoverOpen}
      apiDomain={emojiPickerConfig.apiDomain}
      isAdminView={emojiPickerConfig.isAdminView}
      projectId={emojiPickerConfig.projectId}
    />
  ) : null;

  const content = items.map(item => {
    if (item.type === 'button') {
      const { id, icon: iconEl, title, shortcut, isActive, onClick, markdownAction } = item.def;
      return (
        <Fragment key={id}>
          {id === 'quote' && (
            <LinkToolbarPopover
              variant={variant}
              markdownSource={markdownSourceModeActive ? markdownSource : null}
            />
          )}
          <ToolbarButton
            onClick={() =>
              markdownSourceModeActive ? runMarkdownAction(markdownAction) : onClick(editor)
            }
            active={!markdownSourceModeActive && isActive(editor)}
            title={title}
            shortcut={shortcut}
            preventMousedownBlur={markdownSourceModeActive}
          >
            {iconEl}
          </ToolbarButton>
        </Fragment>
      );
    }
    const { id, getLabel, title, isActive, options } = item.def;
    return (
      <DropdownButton
        key={id}
        label={getLabel(editor)}
        title={title}
        active={!markdownSourceModeActive && isActive(editor)}
        isOpen={openId === id}
        onToggle={() => openOnly(id)}
        variant={variant}
      >
        {options.map(opt => (
          <ToolbarTooltip key={opt.id} label={opt.label} shortcut={opt.shortcut}>
            <button
              type="button"
              className={cx(styles.dropdownItem, {
                [styles.active]: !markdownSourceModeActive && opt.isActive(editor)
              })}
              onMouseDown={e => {
                e.preventDefault();
                if (markdownSourceModeActive) {
                  runMarkdownAction(opt.markdownAction);
                  setOpenId(null);
                } else {
                  opt.onSelect(editor, () => setOpenId(null));
                }
              }}
            >
              {opt.icon ?? null}
              <span>{opt.label}</span>
            </button>
          </ToolbarTooltip>
        ))}
      </DropdownButton>
    );
  });

  return (
    <div className={cx(styles.toolbar, bottomToolbarClass)}>
      {content}
      {markdownButton}
      {emojiButton}
    </div>
  );
};
